// src/utils/telemetry.ts
// Lazy-loaded telemetry module - reduces initial bundle by ~150KB

import type { Span, SpanOptions } from "@opentelemetry/api";
import type { Faro } from "@grafana/faro-web-sdk";
import type { Middleware } from "openapi-fetch";

/**
 * SpanStatusCode enum - matches @opentelemetry/api values
 * Defined here to avoid importing the full package synchronously
 */
export const SpanStatusCode = {
	UNSET: 0,
	OK: 1,
	ERROR: 2,
} as const;

export type SpanStatusCode =
	(typeof SpanStatusCode)[keyof typeof SpanStatusCode];

// Telemetry state
let isInitialized = false;
let initPromise: Promise<void> | null = null;

// Faro instance + the identity to attach to sessions/replays. Auth state can
// resolve before or after initTelemetry() finishes, so the latest user is held
// here and (re)applied once Faro is available.
let faroInstance: Faro | null = null;
let pendingUser: { id: string; email: string } | null = null;

function applyTelemetryUser(): void {
	const faro =
		faroInstance ?? (window as Window & { faro?: Faro }).faro ?? null;
	if (!faro) {
		return;
	}
	if (pendingUser) {
		faro.api.setUser({ id: pendingUser.id, email: pendingUser.email });
	} else {
		faro.api.resetUser();
	}
}

/**
 * Attach (or clear) the authenticated user's identity on Faro so session
 * replays are searchable by email. Safe to call before telemetry is ready —
 * the identity is applied once Faro initializes.
 */
export function setTelemetryUser(
	user: { id: string; email: string } | null,
): void {
	pendingUser = user;
	applyTelemetryUser();
}

// Lazy-loaded modules (populated after init)
let traceApi: typeof import("@opentelemetry/api") | null = null;
let contextApi: typeof import("@opentelemetry/api") | null = null;
let propagationApi: typeof import("@opentelemetry/api") | null = null;

// Active spans for middleware
const activeSpans = new Map<string, Span>();

/**
 * No-op span for use before telemetry is loaded
 */
const noopSpan: Span = {
	spanContext: () => ({
		traceId: "",
		spanId: "",
		traceFlags: 0,
	}),
	setAttribute: () => noopSpan,
	setAttributes: () => noopSpan,
	addEvent: () => noopSpan,
	addLink: () => noopSpan,
	addLinks: () => noopSpan,
	setStatus: () => noopSpan,
	updateName: () => noopSpan,
	end: () => {},
	isRecording: () => false,
	recordException: () => {},
};

/** Same-origin collector path, terminated by the oneDeploy gateway. */
const FARO_COLLECT_URL = "/.well-known/faro/collect";

/**
 * How long to stop shipping after the collector rejects a batch with a 5xx.
 *
 * Faro batches every 250ms and its transport backs off only for 429, so an
 * unreachable collector produced a request every quarter-second for the whole
 * session. During an outage that buried the one error that actually mattered
 * under thousands of identical 503s — the reason this exists is diagnosability,
 * not load.
 */
const COLLECTOR_BACKOFF_MS = 60_000;

/** Whether we have already said out loud that telemetry stopped shipping. */
let collectorFailureReported = false;

/**
 * The two members of `FetchTransport` this module reaches into.
 *
 * Both are declared `private` in Faro's types, so there is no supported way to
 * express this — and the alternatives are worse. Subclassing does not compile
 * for the same reason; writing our own transport would mean reimplementing the
 * keepalive budget, the beacon fallback for oversized bodies, compression and
 * the promise buffer, none of which we want to own. So this is a deliberate,
 * narrow reach into one known implementation, guarded at runtime below.
 */
interface FetchTransportInternals {
	handleResponse(response: Response): Promise<Response>;
	disabledUntil: Date;
}

/**
 * The collector transport, with Faro's own rate-limit backoff extended to 5xx.
 *
 * Faro already has the machinery: `FetchTransport.send()` short-circuits while
 * `disabledUntil` is in the future, and `handleResponse` is the single funnel
 * every response passes through. It just sets `disabledUntil` for 429 alone, so
 * a collector answering 503 got no backoff at all and the 250ms batch flush
 * retried it for the entire session. Extending that one branch is the whole fix.
 *
 * If a future Faro renames the method, the guard below leaves telemetry working
 * exactly as it does today and says so once, rather than failing silently.
 */
function makeCollectorTransport(
	faroSdk: typeof import("@grafana/faro-web-sdk"),
): InstanceType<typeof faroSdk.FetchTransport> {
	const transport = new faroSdk.FetchTransport({ url: FARO_COLLECT_URL });
	const internals = transport as unknown as FetchTransportInternals;
	const originalHandleResponse = internals.handleResponse;

	if (typeof originalHandleResponse !== "function") {
		console.warn(
			"[Telemetry] Faro's FetchTransport no longer exposes handleResponse; collector 5xx backoff is inactive.",
		);
		return transport;
	}

	internals.handleResponse = async (response: Response): Promise<Response> => {
		if (response.status >= 500) {
			internals.disabledUntil = new Date(Date.now() + COLLECTOR_BACKOFF_MS);
			if (!collectorFailureReported) {
				collectorFailureReported = true;
				// Once, deliberately. The transport logs its own line per skipped
				// batch through the UNPATCHED console (so it cannot feed
				// ConsoleInstrumentation back into the queue), but that is still one
				// line per window. This is the single line that says telemetry has
				// stopped being delivered — enough to be discoverable, not a flood.
				console.warn(
					`[Telemetry] Collector returned ${response.status}; pausing delivery in ${COLLECTOR_BACKOFF_MS / 1000}s windows.`,
				);
			}
		}
		return originalHandleResponse.call(transport, response);
	};

	return transport;
}

/**
 * Initialize telemetry (Faro + OpenTelemetry)
 * Call this early in app lifecycle, but it won't block rendering
 */
export async function initTelemetry(): Promise<void> {
	if (isInitialized) {
		return;
	}
	if (initPromise) {
		return initPromise;
	}

	initPromise = (async () => {
		try {
			// Parallel import of all telemetry packages
			const [otelApi, faroSdk, faroTracing, zoneContext, faroReplay] =
				await Promise.all([
					import("@opentelemetry/api"),
					import("@grafana/faro-web-sdk"),
					import("@grafana/faro-web-tracing"),
					import("@opentelemetry/context-zone"),
					import("@grafana/faro-instrumentation-replay"),
				]);

			// Store references for later use
			traceApi = otelApi;
			contextApi = otelApi;
			propagationApi = otelApi;

			// Initialize Faro if not already done (skip in dev — receiver not running locally)
			if (
				!import.meta.env.DEV &&
				!(window as Window & { faro?: unknown }).faro
			) {
				faroInstance = faroSdk.initializeFaro({
					// `transports` and `url` are mutually exclusive — passing both
					// makes Faro log a config error — so the URL moves into the
					// transport. `ignoreUrls` then has to name it explicitly, because
					// Faro only derives that entry from a top-level `url`.
					transports: [makeCollectorTransport(faroSdk)],
					ignoreUrls: [FARO_COLLECT_URL],
					app: {
						name: "oneai-frontend",
						version: "0.1.6-alpha",
					},
					sessionTracking: { samplingRate: 1 },
					instrumentations: [
						...faroSdk.getWebInstrumentations(),
						new faroTracing.TracingInstrumentation({
							instrumentations: [],
							contextManager: new zoneContext.ZoneContextManager(),
						}),
						new faroReplay.ReplayInstrumentation({
							samplingRate: 1,
							inlineStylesheet: true,
							maskTextSelector: "[data-faro-mask]",
							maskAllInputs: false,
						}),
					],
				});
			}

			isInitialized = true;

			// Attach any identity that auth resolved before Faro was ready.
			applyTelemetryUser();

			if (import.meta.env.DEV) {
				console.debug("[Telemetry] Initialized successfully");
			}
		} catch (e) {
			console.error("[Telemetry] Failed to initialize:", e);
			// Don't throw - telemetry failure shouldn't break the app
		}
	})();

	return initPromise;
}

/**
 * Get a tracer instance (returns no-op tracer if not initialized)
 */
export function getTracer(name: string) {
	return {
		startSpan(spanName: string, options?: SpanOptions): Span {
			if (!traceApi) {
				return noopSpan;
			}
			return traceApi.trace.getTracer(name).startSpan(spanName, options);
		},

		async startActiveSpan<T>(
			spanName: string,
			fn: (span: Span) => Promise<T>,
		): Promise<T> {
			if (!traceApi) {
				return fn(noopSpan);
			}
			return traceApi.trace
				.getTracer(name)
				.startActiveSpan(spanName, async (span) => {
					try {
						return await fn(span);
					} finally {
						span.end();
					}
				});
		},
	};
}

/**
 * Get the currently active span (or no-op span)
 */
export function getActiveSpan(): Span {
	if (!traceApi) {
		return noopSpan;
	}
	return traceApi.trace.getActiveSpan() ?? noopSpan;
}

/**
 * Tracing middleware for fetch client
 * Works as no-op until telemetry is loaded
 */
export const traceMiddleware: Middleware = {
	onRequest({ request, id }) {
		if (!traceApi || !contextApi || !propagationApi) {
			return;
		}

		const span = traceApi.trace
			.getTracer("api-tracer")
			.startSpan(`HTTP ${request.method}`, {
				kind: traceApi.SpanKind.CLIENT,
			});

		const subcontext = traceApi.trace.setSpan(
			contextApi.context.active(),
			span,
		);
		span.setAttribute("http.url", request.url);
		activeSpans.set(id, span);

		propagationApi.propagation.inject(subcontext, request.headers, {
			set(carrier, key, value) {
				carrier.set(key, value);
			},
		});
	},

	onError({ error, id }) {
		const span = activeSpans.get(id);
		if (span && traceApi) {
			span.recordException(
				error instanceof Error ? error : new Error(String(error)),
			);
			span.setStatus({
				code: traceApi.SpanStatusCode.ERROR,
				message: error instanceof Error ? error.message : String(error),
			});
			span.end();
			activeSpans.delete(id);
		}
	},

	onResponse({ response, id }) {
		const span = activeSpans.get(id);
		if (span) {
			span.setAttribute("http.status_code", response.status);
			span.end();
			activeSpans.delete(id);
		}
	},
};

/**
 * Check if telemetry is ready
 */
export function isTelemetryReady(): boolean {
	return isInitialized;
}
